import React, { useEffect, useRef, useState } from 'react';
import { Play, Pause, VolumeX } from '../ui/icons';

/**
 * LivingFrame — the homepage's principal visual: a framed, gently changing
 * memorial-video preview so visitors immediately see what Mixtape Memories creates.
 *
 * STRUCTURE — the brand book's hero collage (Figma "04 · Patterns", node 2058:8142):
 * three polaroid-style snapshots on the page (6px paper border, 6px radius, soft
 * photo shadow, small tilts). The two at the back are still photographs; the large
 * one at the front PLAYS THE VIDEO — the original Living Frame from before the
 * rebrand (`LivingVideo` below), with its captions, closing card and controls inside
 * the photo. No separate caption label sits under the collage.
 *
 * The video is NOT a real <video> and does NOT reanimate the people in the photos —
 * it is a restrained scene-by-scene cross-fade with a slow (≤3%) Ken-Burns zoom.
 *
 * Behaviour (unchanged from the original):
 *  - Autoplays MUTED only when in/near the viewport; pauses off-screen or when the
 *    tab is hidden. Never autoplays sound (there is no soundtrack — see below).
 *  - Respects prefers-reduced-motion: shows the strongest still with working manual
 *    controls and no automatic pans/zooms/transitions.
 *  - Media-failure safe: a failed photo falls back to a bundled gradient, so no
 *    snapshot ever shows an empty box.
 *  - Each scene carries its own one-line caption so the rotation reads as a single
 *    life arc — younger years → family life → grandchildren → later years.
 *
 * IMAGERY:
 *  - A coherent sequence of ONE fictional woman (Margaret Ellery) across her life.
 *    These are founder-supplied, fictional-person AI photographs (no real
 *    individuals); commercial licensing is the founder's to confirm. They live under
 *    /preview/livingframe/.
 *  - Five photo scenes, then a sixth CLOSING FRAME: a calm title card ("In loving
 *    memory of" · name · dates) that bookends the video — exactly the "closing frame" a
 *    creator can add in the editor.
 *  - To add or reorder a photo scene, drop the image at
 *    /preview/livingframe/margaret-ellery-<name>.webp and add/move one
 *    { src, caption } entry in SCENES; the closing card is the { card: true } entry.
 *
 * SOUND — the playback bar shows a non-interactive "muted" indicator rather than a
 * mute toggle: there is no licensed soundtrack asset yet, and a mute control with
 * no audio would be a dead control.
 */
const FALLBACK = `${process.env.PUBLIC_URL}/preview/dawn.svg`;
const IMG = (name) => `${process.env.PUBLIC_URL}/preview/livingframe/${name}`;
// Every photo here has a 640px-wide copy beside it (<name>-640.webp, written by
// scripts/optimise-images.js) so phones download roughly a quarter of the bytes.
// The originals are capped at 1280px. Re-run that script after adding a photo.
const SRCSET = (src) => `${src.replace(/\.webp$/, '-640.webp')} 640w, ${src} 1280w`;
// The slideshow photos also have a 960px copy, for phones with sharper screens
// (otherwise they jump from 640 straight to the 1280 original).
const VIDEO_SRCSET = (src) =>
  `${src.replace(/\.webp$/, '-640.webp')} 640w, ${src.replace(/\.webp$/, '-960.webp')} 960w, ${src} 1280w`;
// The slideshow's real width, measured 5 Oct 2026: about 85% of the screen up to
// 640px, at most 524px up to 1023px, and at most 500px from 1024px (two columns).
// It used to say 100vw, so phones picked the 1280px photo where 640px is as sharp
// (PageSpeed "Improve image delivery", ~320 KB). scripts/prerender.mjs preloads the
// first photo with these same values; the e2e suite checks the two agree.
export const VIDEO_SIZES = '(min-width: 1024px) 500px, (min-width: 640px) 524px, 86vw';
const SNAPSHOT_SIZES = '(min-width: 1024px) 280px, 46vw';

// Fictional demo identity — NOT a real person, and not real dates.
const TITLE = 'Margaret Ellery';
const LIFESPAN = '1946 – 2024';
// Closing card wording (founder, 2026-09-27): "In loving memory of" above the name.
const CLOSING_LEAD = 'In loving memory of';

// One woman across her life — the rotation is the story, each caption a beat in it —
// closing on a title card so visitors learn what a "closing frame" is.
const SCENES = [
  { src: IMG('margaret-ellery-younger-years.webp'),    caption: 'Twenty-six, and the whole garden in bloom.' },
  { src: IMG('margaret-ellery-family.webp'),           caption: 'A husband, two children and a house full of laughter.' },
  { src: IMG('margaret-ellery-grandchildren.webp'),    caption: 'The first grandchild, and Sunday lunches that ran long.' },
  { src: IMG('margaret-ellery-later-years.webp'),      caption: 'Fifty years on, three generations gathered around her.' },
  { src: IMG('margaret-ellery-family-gathering.webp'), caption: 'The whole family she made, together in the garden.' },
  { card: true }, // closing frame: name · dates · closing message
  // A moment of nothing before the loop starts again (founder, 28 Sep): the closing
  // card fades out completely to an empty frame BEFORE the first photo and its
  // name/caption come back, so the name never shows twice at once.
  { blank: true, ms: 1500 },
];
const SCENE_MS = 3000; // default per-scene dwell / gentle Ken-Burns cycle
const FADE_MS = 1200;  // cross-fade between scenes; the blank pause is longer than this
const TICK_MS = 100;
const durationOf = (i) => SCENES[i].ms || SCENE_MS;
const TOTAL_MS = SCENES.reduce((sum, _s, i) => sum + durationOf(i), 0);
const startOf = (i) => SCENES.slice(0, i).reduce((sum, _s, j) => sum + durationOf(j), 0);

// The two still snapshots behind the video (Figma tilts: +6°, −4°; the video +2°).
const BACK_SNAPSHOTS = [
  { src: IMG('margaret-ellery-family.webp'),        className: 'right-0 top-0 w-[46%]', rotate: 6 },
  { src: IMG('margaret-ellery-grandchildren.webp'), className: 'left-0 top-[3%] w-[44%]', rotate: -4 },
];

const INK_SCRIM = 'linear-gradient(to top, rgba(36,26,18,0.94) 0%, rgba(36,26,18,0.72) 22%, rgba(36,26,18,0.28) 46%, rgba(36,26,18,0) 66%)';

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** A still polaroid behind the video. Decorative — the video carries the meaning. */
const Snapshot = ({ src, className, rotate }) => {
  const [failed, setFailed] = useState(false);
  return (
    <div
      aria-hidden="true"
      data-snapshot="still"
      className={`absolute ${className} bg-brand-cream p-[6px] rounded-[6px] shadow-photo`}
      style={{ transform: `rotate(${rotate}deg)` }}
    >
      <img
        src={failed ? FALLBACK : src}
        srcSet={failed ? undefined : SRCSET(src)}
        sizes={SNAPSHOT_SIZES}
        width={1280}
        height={960}
        alt=""
        decoding="async"
        onError={() => setFailed(true)}
        className="block w-full aspect-[4/3] object-cover rounded-[2px] bg-brand-rose"
      />
    </div>
  );
};

/** The original Living Frame video, unchanged apart from brand colours and fonts. */
const LivingVideo = () => {
  const reduced = prefersReducedMotion();
  const rootRef = useRef(null);
  const [inView, setInView] = useState(false);
  const [playing, setPlaying] = useState(false);
  // One state for both, so moving to the next scene and resetting the clock always
  // happen together (each scene has its own length).
  const [pos, setPos] = useState({ scene: 0, elapsed: 0 }); // elapsed: ms within the scene
  const { scene, elapsed } = pos;
  const [failed, setFailed] = useState({});   // sceneIndex -> true
  // Photos download just in time: the first two at once, then each one while the
  // scene before it is showing (every scene lasts several seconds), instead of all
  // five as the page opens. A photo stays once it has loaded.
  const [reached, setReached] = useState(0);
  useEffect(() => { setReached(r => Math.max(r, scene)); }, [scene]);

  // Play only when in/near the viewport.
  useEffect(() => {
    if (!rootRef.current || typeof IntersectionObserver === 'undefined') { setInView(true); return undefined; }
    const io = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { rootMargin: '200px', threshold: 0.25 }
    );
    io.observe(rootRef.current);
    return () => io.disconnect();
  }, []);

  // Never autoplay under reduced motion; otherwise follow viewport visibility.
  useEffect(() => {
    setPlaying(reduced ? false : inView);
  }, [inView, reduced]);

  // Pause when the browser tab is hidden.
  useEffect(() => {
    const onVis = () => {
      if (document.hidden) setPlaying(false);
      else if (!reduced && inView) setPlaying(true);
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [inView, reduced]);

  // Advance progress + scenes while playing.
  useEffect(() => {
    if (!playing) return undefined;
    const t = setInterval(() => {
      setPos(({ scene: s, elapsed: e }) => {
        const next = e + TICK_MS;
        return next >= durationOf(s)
          ? { scene: (s + 1) % SCENES.length, elapsed: 0 }
          : { scene: s, elapsed: next };
      });
    }, TICK_MS);
    return () => clearInterval(t);
  }, [playing]);

  const overallPct = (Math.min(startOf(scene) + elapsed, TOTAL_MS) / TOTAL_MS) * 100;
  const srcFor = (i) => (failed[i] ? FALLBACK : SCENES[i].src);
  // On the closing card (and the empty pause after it) the name is on the card, or
  // nowhere: the usual title/caption overlay stays hidden.
  const activeIsCard = !!(SCENES[scene].card || SCENES[scene].blank);
  const caption = SCENES[scene].caption;

  return (
    <div
      ref={rootRef}
      data-snapshot="video"
      className="relative w-full aspect-video overflow-hidden rounded-[2px] bg-brand-ink"
    >
      {/* Cross-fading scenes — five photographs, then a closing title card */}
      {SCENES.map((s, i) => {
        const active = i === scene;
        if (s.blank) return null; // the empty pause: only the ink background shows
        if (!s.card && i > reached + 1) return null; // not needed yet (see `reached`)
        if (s.card) {
          // Closing frame: a calm title card (name · dates · message), exactly
          // like the optional closing frame a creator can add in the editor.
          // Hidden from assistive tech only while it isn't the visible scene —
          // on this scene the usual title/caption overlay is removed, so the
          // card itself is what a screen reader should read.
          return (
            <div
              key="closing-card"
              data-closing-card
              aria-hidden={active ? undefined : true}
              className="absolute inset-0 flex flex-col items-center justify-center text-center px-6 bg-brand-ink"
              style={{ opacity: active ? 1 : 0, transition: `opacity ${FADE_MS}ms ease-in-out` }}
            >
              <p className="font-script text-brand-blush mb-2" style={{ fontSize: 'clamp(1.6rem, 3.2vw, 2.4rem)', lineHeight: 1 }}>{CLOSING_LEAD}</p>
              <p className="font-display text-white leading-tight" style={{ fontSize: 'clamp(1.25rem, 2.4vw, 1.9rem)' }}>{TITLE}</p>
              <p className="font-sans text-white/70 text-label tracking-widest mt-2">{LIFESPAN}</p>
            </div>
          );
        }
        return (
          <img
            key={s.src}
            src={srcFor(i)}
            srcSet={failed[i] ? undefined : VIDEO_SRCSET(s.src)}
            sizes={VIDEO_SIZES}
            width={1280}
            height={720}
            // The first photo is what the visitor sees first; the rest can wait.
            fetchPriority={i === 0 ? 'high' : 'low'}
            decoding="async"
            alt=""
            aria-hidden="true"
            onError={() => setFailed(f => (f[i] ? f : { ...f, [i]: true }))}
            className="absolute inset-0 w-full h-full object-cover"
            style={{
              opacity: active ? 1 : 0,
              transform: (!reduced && active && playing) ? 'scale(1.03)' : 'scale(1)',
              transition: `opacity ${FADE_MS}ms ease-in-out${reduced ? '' : `, transform ${SCENE_MS}ms linear`}`,
            }}
          />
        );
      })}

      {/* Bottom scrim so title/caption/controls stay legible over any photograph. */}
      <div className="absolute inset-0 pointer-events-none" style={{ background: INK_SCRIM }} />

      {/* Title, caption + playback bar, one compact bottom-anchored stack.
          The name is text inside the demo video, not a heading of the page: as an
          <h3> it sat straight under the page's <h1> and skipped a level (PageSpeed
          "Heading elements are not in a sequentially-descending order", 5 Oct 2026).
          The closing card's name is a <p> for the same reason.
          On the closing card the name/dates already sit centred, so only the
          playback bar shows here. */}
      <div className="absolute inset-x-0 bottom-0 px-3 sm:px-4 pb-2.5 sm:pb-3">
        {!activeIsCard && (
          <>
            <p className="font-display text-white leading-tight" style={{ fontSize: 'clamp(1rem, 1.9vw, 1.35rem)' }}>
              {TITLE}
              <span className="ml-2 font-sans text-white/80 text-label tracking-widest align-middle whitespace-nowrap">{LIFESPAN}</span>
            </p>
            <p className="font-sans text-brand-cream/90 text-xs sm:text-sm mt-0.5 leading-snug [text-wrap:balance]">{caption}</p>
          </>
        )}

        <div className="mt-2 flex items-center gap-3">
          <button
            type="button"
            onClick={() => setPlaying(p => !p)}
            aria-label={playing ? 'Pause preview' : 'Play preview'}
            aria-pressed={playing}
            className="flex-shrink-0 w-8 h-8 rounded-full bg-white/15 hover:bg-white/25 ring-1 ring-white/30 backdrop-blur-sm flex items-center justify-center text-white transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
          >
            {playing ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5 ml-0.5" />}
          </button>

          <div
            className="flex-1 h-1 rounded-full bg-white/25 overflow-hidden"
            role="progressbar"
            aria-label="Preview progress"
            aria-valuenow={Math.round(overallPct)}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <div className="h-full bg-brand-coral" style={{ width: `${overallPct}%`, transition: 'width 120ms linear' }} />
          </div>

          {/* Honest muted indicator (not a dead toggle — no soundtrack asset yet) */}
          <span className="flex-shrink-0 inline-flex items-center text-white/70" title="This preview plays without sound">
            <VolumeX className="w-4 h-4" aria-hidden="true" />
            <span className="sr-only">Muted preview</span>
          </span>
        </div>
      </div>
    </div>
  );
};

const LivingFrame = () => (
  <figure className="m-0" aria-label="Example of a memorial video you can create">
    {/* The back snapshots peek out above the video: the top padding makes room. */}
    <div className="relative pt-[17%] pb-2">
      {BACK_SNAPSHOTS.map(s => <Snapshot key={s.src} {...s} />)}

      {/* Front snapshot: the video, in a polaroid border, on top of the pile. */}
      <div
        className="relative z-10 ml-[3%] w-[94%] bg-brand-cream p-[6px] rounded-[6px] shadow-photo"
        style={{ transform: 'rotate(2deg)' }}
      >
        <LivingVideo />
      </div>
    </div>
  </figure>
);

export default LivingFrame;
